'use client';

import React, { useState } from 'react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Loader2, ArrowRightLeft } from 'lucide-react';
import { authFetch } from '@/lib/auth-fetch';

interface TransferProjectDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  projectId: string;
  projectName: string;
  onTransferred: () => void;
}

export function TransferProjectDialog({
  isOpen,
  onOpenChange,
  projectId,
  projectName,
  onTransferred,
}: TransferProjectDialogProps) {
  const [targetEmail, setTargetEmail] = useState('');
  const [isTransferring, setIsTransferring] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!targetEmail.trim()) {
      setError('Please enter an email address.');
      return;
    }

    setIsTransferring(true);
    setError(null);

    try {
      const res = await authFetch(`/api/projects/${projectId}/transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetEmail: targetEmail.trim() }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Transfer failed.');
      }

      onTransferred();
      onOpenChange(false);
    } catch (err: any) {
      setError(err.message || 'Transfer failed.');
    } finally {
      setIsTransferring(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setTargetEmail('');
      setError(null);
    }
    onOpenChange(open);
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Transfer Project</AlertDialogTitle>
          <AlertDialogDescription>
            Transfer &quot;{projectName}&quot; to another user. This will move the entire project
            and all its data to the target user&apos;s account. You will lose access to it.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-2">
            <Label htmlFor="transfer-email">Recipient&apos;s email address</Label>
            <Input
              id="transfer-email"
              type="email"
              placeholder="user@example.com"
              value={targetEmail}
              onChange={(e) => {
                setTargetEmail(e.target.value);
                setError(null);
              }}
              disabled={isTransferring}
              autoFocus
            />
          </div>
          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isTransferring}>Cancel</AlertDialogCancel>
          <Button
            onClick={handleConfirm}
            disabled={isTransferring || !targetEmail.trim()}
          >
            {isTransferring ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Transferring...
              </>
            ) : (
              <>
                <ArrowRightLeft className="mr-2 h-4 w-4" />
                Transfer
              </>
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
